import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Sparkles, Dumbbell, Utensils, Loader2, Save, Calendar, ShoppingCart, Clock,
  Zap, Target, ChevronRight, ArrowRight, CheckCircle2, Brain, Flame, Copy, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Paywall from "@/components/Paywall";

// Shopping List component with copy functionality
function ShoppingListCard({ items }) {
  const [copied, setCopied] = useState(false);

  const formatForCopy = () => {
    const header = "🛒 INDKØBSLISTE - FitrackR\n" + "━".repeat(30) + "\n\n";
    const itemsList = items
      .map(item => `☐ ${item.name} - ${item.amount}`)
      .join("\n");
    const footer = "\n\n" + "━".repeat(30) + "\nGenereret af FitrackR.dk";
    return header + itemsList + footer;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formatForCopy());
      setCopied(true);
      toast.success("Indkøbsliste kopieret!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = formatForCopy();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Indkøbsliste kopieret!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareList = async () => {
    const text = formatForCopy();
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Indkøbsliste - FitrackR",
          text: text
        });
      } catch (err) {
        // User cancelled or share failed, fallback to copy
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  return (
    <div className="mt-6 p-4 bg-purple-500/5 rounded-xl border border-purple-500/10">
      <div className="flex items-center justify-between mb-4">
        <h4 style={{ fontFamily: 'Oswald' }} className="uppercase text-sm text-purple-400 flex items-center gap-2">
          <ShoppingCart className="h-4 w-4" /> Indkøbsliste ({items.length} varer)
        </h4>
        <div className="flex gap-2">
          <Button
            onClick={copyToClipboard}
            size="sm"
            variant="outline"
            className={`h-8 px-3 text-xs rounded-lg border-purple-500/30 transition-all ${
              copied 
                ? 'bg-green-500/20 border-green-500/30 text-green-400' 
                : 'hover:bg-purple-500/10 text-purple-400'
            }`}
            data-testid="copy-shopping-list"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 mr-1.5" />
                Kopieret!
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                Kopiér liste
              </>
            )}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((item, i) => (
          <div key={i} className="flex justify-between text-xs bg-black/20 rounded px-2 py-1.5">
            <span className="text-zinc-300">{item.name}</span>
            <span className="text-zinc-500">{item.amount}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-zinc-600 mt-3 text-center">
        Tip: Tryk "Kopiér liste" og indsæt i din foretrukne noter-app
      </p>
    </div>
  );
}

// Loading component with progress
function AILoadingState({ message, estimatedTime }) {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const progress = Math.min((elapsed / estimatedTime) * 100, 95);

  return (
    <div className="bg-gradient-to-br from-purple-500/10 via-[#18181b] to-[#18181b] border border-purple-500/20 rounded-2xl p-8 text-center">
      <div className="relative w-24 h-24 mx-auto mb-6">
        <div className="absolute inset-0 rounded-full border-4 border-zinc-800" />
        <div className="absolute inset-0 rounded-full border-4 border-purple-500 border-t-transparent animate-spin" />
        <div className="absolute inset-2 rounded-full bg-purple-500/10 flex items-center justify-center">
          <Brain className="h-10 w-10 text-purple-400 animate-pulse" />
        </div>
      </div>
      <p className="font-bold text-white text-lg mb-2">{message}</p>
      <div className="max-w-xs mx-auto">
        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-2">
          <div className="h-full bg-gradient-to-r from-purple-500 to-[#DFFF00] transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
          <Clock className="h-3 w-3" />
          <span>{elapsed}s / ~{estimatedTime}s</span>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mt-4">AI analyserer dine præferencer og genererer...</p>
    </div>
  );
}

// Mode selection card
function ModeCard({ icon: Icon, title, description, isActive, onClick, color, badge }) {
  return (
    <button
      onClick={onClick}
      className={`relative w-full p-5 rounded-xl border transition-all duration-300 text-left group ${
        isActive 
          ? `bg-gradient-to-br ${color} border-white/20 shadow-lg` 
          : 'bg-[#18181b] border-white/5 hover:border-white/10'
      }`}
    >
      {badge && (
        <span className="absolute -top-2 -right-2 bg-[#DFFF00] text-black text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
          {badge}
        </span>
      )}
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 transition-transform group-hover:scale-110 ${
        isActive ? 'bg-white/20' : 'bg-white/5'
      }`}>
        <Icon className={`h-6 w-6 ${isActive ? 'text-white' : 'text-zinc-400'}`} />
      </div>
      <h3 className={`font-bold mb-1 ${isActive ? 'text-white' : 'text-zinc-300'}`}>{title}</h3>
      <p className={`text-xs ${isActive ? 'text-white/70' : 'text-zinc-500'}`}>{description}</p>
      <ChevronRight className={`absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-all ${
        isActive ? 'text-white/50 translate-x-1' : 'text-zinc-600 opacity-0 group-hover:opacity-100'
      }`} />
    </button>
  );
}

export default function AICoach() {
  const { authHeaders } = useAuth();
  const [activeMode, setActiveMode] = useState("workout");

  // Workout states
  const [wGoal, setWGoal] = useState("styrke");
  const [wLevel, setWLevel] = useState("intermediate");
  const [wEquipment, setWEquipment] = useState("Fuldt fitnesscenter");
  const [wDuration, setWDuration] = useState("60");
  const [wResult, setWResult] = useState(null);
  const [wLoading, setWLoading] = useState(false);

  // Meal states
  const [mGoal, setMGoal] = useState("maintaining");
  const [mCalories, setMCalories] = useState("2000");
  const [mDiet, setMDiet] = useState("Ingen begrænsninger");
  const [mMeals, setMMeals] = useState("4");
  const [mResult, setMResult] = useState(null);
  const [mLoading, setMLoading] = useState(false);

  // Weekly states
  const [weeklyMealResult, setWeeklyMealResult] = useState(null);
  const [weeklyMealLoading, setWeeklyMealLoading] = useState(false);
  const [savingWeeklyMeal, setSavingWeeklyMeal] = useState(false);
  const [weeklyWorkoutResult, setWeeklyWorkoutResult] = useState(null);
  const [weeklyWorkoutLoading, setWeeklyWorkoutLoading] = useState(false);
  const [wwDays, setWwDays] = useState("5");

  const generateWorkout = async () => {
    setWLoading(true); setWResult(null);
    try {
      const res = await axios.post(`${API}/ai/workout`, {
        goal: wGoal, experience_level: wLevel, available_equipment: wEquipment,
        focus_areas: [], duration_minutes: parseInt(wDuration),
      }, { headers: authHeaders() });
      setWResult(res.data.workout);
      toast.success("Træningsprogram genereret!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved AI generering");
    } finally { setWLoading(false); }
  };

  const generateMealplan = async () => {
    setMLoading(true); setMResult(null);
    try {
      const res = await axios.post(`${API}/ai/mealplan`, {
        goal: mGoal, calories_target: parseInt(mCalories),
        dietary_preferences: mDiet, meals_per_day: parseInt(mMeals),
      }, { headers: authHeaders() });
      setMResult(res.data.mealplan);
      toast.success("Madplan genereret!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved AI generering");
    } finally { setMLoading(false); }
  };

  const saveMealplan = async () => {
    if (!mResult) return;
    try {
      await axios.post(`${API}/meals`, {
        name: mResult.name || "AI Madplan", description: mResult.description || "",
        meals: mResult.meals || [], total_calories: mResult.total_calories || 0,
        total_protein: mResult.total_protein || 0, total_carbs: mResult.total_carbs || 0,
        total_fat: mResult.total_fat || 0, ai_generated: true,
      }, { headers: authHeaders() });
      toast.success("Madplan gemt!");
    } catch { toast.error("Fejl ved gemning"); }
  };

  const saveWorkout = async () => {
    if (!wResult?.exercises) return;
    try {
      await axios.post(`${API}/workouts`, {
        name: wResult.name || "AI Træning",
        exercises: wResult.exercises.map(e => ({
          exercise_name: e.exercise_name, sets: e.sets || [{ reps: 10, weight: 0 }],
        })),
      }, { headers: authHeaders() });
      toast.success("Træning gemt i din log!");
    } catch { toast.error("Fejl ved gemning"); }
  };

  const generateWeeklyMealplan = async () => {
    setWeeklyMealLoading(true); setWeeklyMealResult(null);
    try {
      const res = await axios.post(`${API}/ai/weekly-mealplan`, {
        goal: mGoal, calories_target: parseInt(mCalories),
        dietary_preferences: mDiet, meals_per_day: parseInt(mMeals),
      }, { headers: authHeaders(), timeout: 180000 });
      setWeeklyMealResult(res.data.mealplan);
      toast.success("Ugentlig madplan genereret!");
    } catch (err) {
      const errorMsg = err.response?.data?.detail || (err.code === 'ECONNABORTED' ? "Timeout - prøv igen" : "AI-serveren er optaget. Prøv igen om lidt.");
      toast.error(errorMsg, { duration: 5000 });
    } finally { setWeeklyMealLoading(false); }
  };

  const generateWeeklyWorkout = async () => {
    setWeeklyWorkoutLoading(true); setWeeklyWorkoutResult(null);
    try {
      const res = await axios.post(`${API}/ai/weekly-workout`, {
        goal: wGoal, experience_level: wLevel, available_equipment: wEquipment,
        days_per_week: parseInt(wwDays), duration_minutes: parseInt(wDuration),
      }, { headers: authHeaders() });
      setWeeklyWorkoutResult(res.data.program);
      toast.success("Ugentligt træningsprogram genereret!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved AI generering");
    } finally { setWeeklyWorkoutLoading(false); }
  };

  const saveWeeklyMealplan = async () => {
    if (!weeklyMealResult) return;
    setSavingWeeklyMeal(true);
    try {
      await axios.post(`${API}/weekly-meals`, {
        ...weeklyMealResult, goal: mGoal, calories_target: parseInt(mCalories),
      }, { headers: authHeaders() });
      toast.success("Ugentlig madplan gemt! Se den under Madplaner.");
    } catch { toast.error("Fejl ved gemning af madplan"); }
    finally { setSavingWeeklyMeal(false); }
  };

  const modes = [
    { id: "workout", icon: Dumbbell, title: "Enkelt Træning", description: "Generér ét træningsprogram", color: "from-[#DFFF00]/20 to-[#DFFF00]/5" },
    { id: "mealplan", icon: Utensils, title: "Daglig Madplan", description: "Få en dags kostplan", color: "from-emerald-500/20 to-emerald-500/5" },
    { id: "weekly-workout", icon: Calendar, title: "Ugeprogram", description: "Komplet ugentlig træning", color: "from-blue-500/20 to-blue-500/5", badge: "Populær" },
    { id: "weekly-meal", icon: ShoppingCart, title: "Ugentlig Kostplan", description: "7 dage + indkøbsliste", color: "from-purple-500/20 to-purple-500/5" },
  ];

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]" data-testid="ai-coach-page">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-purple-500/10 via-transparent to-transparent" />
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-64 h-64 bg-[#DFFF00]/10 rounded-full blur-3xl" />
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 mb-4">
                <Sparkles className="h-4 w-4 text-purple-400" />
                <span className="text-sm text-purple-300">Powered by AI</span>
              </div>
              <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-4xl sm:text-5xl uppercase tracking-tight text-white mb-3">
                Din <span className="text-[#DFFF00]">Personlige</span> AI Coach
              </h1>
              <p className="text-zinc-400 max-w-xl mx-auto">
                Få skræddersyede træningsprogrammer og madplaner baseret på dine mål og præferencer
              </p>
            </div>

            {/* Mode Selection */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {modes.map(mode => (
                <ModeCard
                  key={mode.id}
                  icon={mode.icon}
                  title={mode.title}
                  description={mode.description}
                  color={mode.color}
                  badge={mode.badge}
                  isActive={activeMode === mode.id}
                  onClick={() => setActiveMode(mode.id)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          
          {/* Single Workout */}
          {activeMode === "workout" && (
            <div className="space-y-6">
              <Card className="bg-[#18181b] border-white/5 rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-[#DFFF00]/10 to-transparent p-6 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center">
                      <Dumbbell className="h-5 w-5 text-[#DFFF00]" />
                    </div>
                    <div>
                      <h2 style={{ fontFamily: 'Oswald' }} className="font-bold text-xl uppercase text-white">Generer Træningsprogram</h2>
                      <p className="text-xs text-zinc-500">Tilpas dine præferencer nedenfor</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                        <Target className="h-3 w-3" /> Mål
                      </Label>
                      <Select value={wGoal} onValueChange={setWGoal}>
                        <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12" data-testid="workout-goal-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="styrke">Styrke</SelectItem>
                          <SelectItem value="muskelopbygning">Muskelopbygning</SelectItem>
                          <SelectItem value="vægttab">Vægttab</SelectItem>
                          <SelectItem value="udholdenhed">Udholdenhed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                        <Flame className="h-3 w-3" /> Niveau
                      </Label>
                      <Select value={wLevel} onValueChange={setWLevel}>
                        <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12" data-testid="workout-level-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="beginner">Begynder</SelectItem>
                          <SelectItem value="intermediate">Middel</SelectItem>
                          <SelectItem value="advanced">Avanceret</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Udstyr</Label>
                      <Input value={wEquipment} onChange={e => setWEquipment(e.target.value)}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-12" data-testid="workout-equipment-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                        <Clock className="h-3 w-3" /> Varighed (min)
                      </Label>
                      <Input type="number" value={wDuration} onChange={e => setWDuration(e.target.value)}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-12" data-testid="workout-duration-input" />
                    </div>
                  </div>
                  <Button onClick={generateWorkout} disabled={wLoading} data-testid="generate-workout-btn"
                    className="w-full mt-6 bg-gradient-to-r from-[#DFFF00] to-[#C8E600] text-black hover:opacity-90 rounded-xl font-bold uppercase tracking-wider h-12 text-sm">
                    {wLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Genererer...</> : <><Sparkles className="mr-2 h-4 w-4" /> Generer program</>}
                  </Button>
                </CardContent>
              </Card>

              {wLoading && <AILoadingState message="Genererer dit træningsprogram" estimatedTime={15} />}

              {wResult && (
                <Card className="bg-[#18181b] border-[#DFFF00]/20 rounded-2xl overflow-hidden" data-testid="workout-result">
                  <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-[#DFFF00]/10 to-transparent border-b border-white/5">
                    <div>
                      <CardTitle style={{ fontFamily: 'Oswald' }} className="uppercase text-xl text-white">{wResult.name}</CardTitle>
                      {wResult.description && <p className="text-sm text-zinc-400 mt-1">{wResult.description}</p>}
                    </div>
                    <Button size="sm" onClick={saveWorkout} data-testid="save-ai-workout" 
                      className="bg-[#DFFF00] text-black hover:bg-[#C8E600] rounded-lg">
                      <Save className="mr-1 h-4 w-4" /> Gem
                    </Button>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      {wResult.exercises?.map((ex, i) => (
                        <div key={i} className="bg-zinc-900/50 rounded-xl p-4 border border-white/5 hover:border-[#DFFF00]/20 transition-colors">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="w-8 h-8 rounded-lg bg-[#DFFF00]/10 flex items-center justify-center">
                              <span style={{ fontFamily: 'JetBrains Mono' }} className="text-sm font-bold text-[#DFFF00]">{i+1}</span>
                            </span>
                            <span className="font-bold text-white">{ex.exercise_name}</span>
                          </div>
                          <div className="flex gap-2 flex-wrap ml-11">
                            {ex.sets?.map((s, j) => (
                              <span key={j} style={{ fontFamily: 'JetBrains Mono' }} 
                                className="text-xs bg-white/5 px-3 py-1.5 rounded-lg text-zinc-300">
                                {s.reps} reps × {s.weight}kg
                              </span>
                            ))}
                          </div>
                          {ex.notes && <p className="text-xs text-zinc-500 mt-2 ml-11">{ex.notes}</p>}
                        </div>
                      ))}
                    </div>
                    {wResult.tips && (
                      <div className="mt-6 p-4 bg-[#DFFF00]/5 rounded-xl border border-[#DFFF00]/10 flex items-start gap-3">
                        <Zap className="h-5 w-5 text-[#DFFF00] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-[#DFFF00]">{wResult.tips}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Single Mealplan */}
          {activeMode === "mealplan" && (
            <div className="space-y-6">
              <Card className="bg-[#18181b] border-white/5 rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500/10 to-transparent p-6 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Utensils className="h-5 w-5 text-emerald-400" />
                    </div>
                    <div>
                      <h2 style={{ fontFamily: 'Oswald' }} className="font-bold text-xl uppercase text-white">Generer Madplan</h2>
                      <p className="text-xs text-zinc-500">En dags kostplan baseret på dine behov</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Mål</Label>
                      <Select value={mGoal} onValueChange={setMGoal}>
                        <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12" data-testid="meal-goal-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="bulking">Bulking (opbygning)</SelectItem>
                          <SelectItem value="cutting">Cutting (vægttab)</SelectItem>
                          <SelectItem value="maintaining">Vedligeholdelse</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Kaloriemål</Label>
                      <Input type="number" value={mCalories} onChange={e => setMCalories(e.target.value)}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-emerald-500 rounded-xl h-12" data-testid="meal-calories-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Kostpræferencer</Label>
                      <Input value={mDiet} onChange={e => setMDiet(e.target.value)}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-emerald-500 rounded-xl h-12" data-testid="meal-diet-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Antal måltider/dag</Label>
                      <Select value={mMeals} onValueChange={setMMeals}>
                        <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12" data-testid="meal-count-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="3">3 måltider</SelectItem>
                          <SelectItem value="4">4 måltider</SelectItem>
                          <SelectItem value="5">5 måltider</SelectItem>
                          <SelectItem value="6">6 måltider</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={generateMealplan} disabled={mLoading} data-testid="generate-mealplan-btn"
                    className="w-full mt-6 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:opacity-90 rounded-xl font-bold uppercase tracking-wider h-12 text-sm">
                    {mLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Genererer...</> : <><Sparkles className="mr-2 h-4 w-4" /> Generer madplan</>}
                  </Button>
                </CardContent>
              </Card>

              {mLoading && <AILoadingState message="Genererer din madplan" estimatedTime={15} />}

              {mResult && (
                <Card className="bg-[#18181b] border-emerald-500/20 rounded-2xl overflow-hidden" data-testid="mealplan-result">
                  <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-emerald-500/10 to-transparent border-b border-white/5">
                    <CardTitle style={{ fontFamily: 'Oswald' }} className="uppercase text-xl text-white">{mResult.name}</CardTitle>
                    <Button size="sm" onClick={saveMealplan} data-testid="save-ai-mealplan" 
                      className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg">
                      <Save className="mr-1 h-4 w-4" /> Gem
                    </Button>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Macros */}
                    <div className="grid grid-cols-4 gap-3 mb-6">
                      {[
                        { label: "Kcal", value: mResult.total_calories, color: "from-[#DFFF00]/20 to-transparent", text: "text-[#DFFF00]" },
                        { label: "Protein", value: `${mResult.total_protein}g`, color: "from-blue-500/20 to-transparent", text: "text-blue-400" },
                        { label: "Kulhydrat", value: `${mResult.total_carbs}g`, color: "from-orange-500/20 to-transparent", text: "text-orange-400" },
                        { label: "Fedt", value: `${mResult.total_fat}g`, color: "from-pink-500/20 to-transparent", text: "text-pink-400" },
                      ].map(({ label, value, color, text }) => (
                        <div key={label} className={`text-center p-4 bg-gradient-to-b ${color} rounded-xl`}>
                          <div style={{ fontFamily: 'JetBrains Mono' }} className={`font-bold text-xl ${text}`}>{value}</div>
                          <div className="text-[10px] uppercase text-zinc-500 mt-1">{label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-3">
                      {mResult.meals?.map((m, i) => (
                        <div key={i} className="bg-zinc-900/50 rounded-xl p-4 border border-white/5">
                          <span className="font-bold text-white">{m.name}</span>
                          <div className="mt-3 space-y-2">
                            {m.foods?.map((f, j) => (
                              <div key={j} className="flex justify-between text-sm text-zinc-400">
                                <span>{f.name} <span className="text-zinc-600">({f.amount})</span></span>
                                <div className="flex gap-4" style={{ fontFamily: 'JetBrains Mono' }}>
                                  <span>{f.calories}kcal</span>
                                  <span className="text-blue-400">{f.protein}p</span>
                                  <span className="text-orange-400">{f.carbs}k</span>
                                  <span className="text-pink-400">{f.fat}f</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {mResult.tips && (
                      <div className="mt-6 p-4 bg-emerald-500/5 rounded-xl border border-emerald-500/10 flex items-start gap-3">
                        <Zap className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-emerald-400">{mResult.tips}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Weekly Workout */}
          {activeMode === "weekly-workout" && (
            <div className="space-y-6">
              <Card className="bg-[#18181b] border-white/5 rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500/10 to-transparent p-6 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <h2 style={{ fontFamily: 'Oswald' }} className="font-bold text-xl uppercase text-white">Ugentligt Træningsprogram</h2>
                      <p className="text-xs text-zinc-500">Komplet ugeplan med splits (f.eks. Push/Pull/Legs)</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Mål</Label>
                      <Select value={wGoal} onValueChange={setWGoal}>
                        <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="styrke">Styrke</SelectItem>
                          <SelectItem value="muskelopbygning">Muskelopbygning</SelectItem>
                          <SelectItem value="vægttab">Vægttab</SelectItem>
                          <SelectItem value="udholdenhed">Udholdenhed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Niveau</Label>
                      <Select value={wLevel} onValueChange={setWLevel}>
                        <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="beginner">Begynder</SelectItem>
                          <SelectItem value="intermediate">Middel</SelectItem>
                          <SelectItem value="advanced">Avanceret</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Dage per uge</Label>
                      <Select value={wwDays} onValueChange={setWwDays}>
                        <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12" data-testid="weekly-days-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="3">3 dage</SelectItem>
                          <SelectItem value="4">4 dage</SelectItem>
                          <SelectItem value="5">5 dage</SelectItem>
                          <SelectItem value="6">6 dage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Varighed per session (min)</Label>
                      <Input type="number" value={wDuration} onChange={e => setWDuration(e.target.value)}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-blue-500 rounded-xl h-12" />
                    </div>
                  </div>
                  <Button onClick={generateWeeklyWorkout} disabled={weeklyWorkoutLoading} data-testid="generate-weekly-workout-btn"
                    className="w-full mt-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:opacity-90 rounded-xl font-bold uppercase tracking-wider h-12 text-sm">
                    {weeklyWorkoutLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Genererer...</> : <><Calendar className="mr-2 h-4 w-4" /> Generer ugeprogram</>}
                  </Button>
                </CardContent>
              </Card>

              {weeklyWorkoutLoading && <AILoadingState message="Genererer dit ugentlige træningsprogram" estimatedTime={20} />}

              {weeklyWorkoutResult && (
                <Card className="bg-[#18181b] border-blue-500/20 rounded-2xl overflow-hidden" data-testid="weekly-workout-result">
                  <CardHeader className="bg-gradient-to-r from-blue-500/10 to-transparent border-b border-white/5">
                    <CardTitle style={{ fontFamily: 'Oswald' }} className="uppercase text-xl text-white">{weeklyWorkoutResult.name}</CardTitle>
                    {weeklyWorkoutResult.split_type && (
                      <span className="inline-block bg-blue-500/20 text-blue-400 text-xs font-bold px-3 py-1 rounded-full mt-2">
                        {weeklyWorkoutResult.split_type}
                      </span>
                    )}
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {weeklyWorkoutResult.days?.map((day, i) => (
                        <div key={i} className="bg-zinc-900/50 rounded-xl p-4 border border-white/5">
                          <div className="flex items-center justify-between mb-3">
                            <span style={{ fontFamily: 'Oswald' }} className="font-bold text-white uppercase">{day.day}</span>
                            <span className="text-xs bg-white/5 px-2 py-1 rounded-lg text-zinc-400">{day.focus}</span>
                          </div>
                          {day.exercises?.length > 0 ? (
                            <div className="space-y-2">
                              {day.exercises.map((ex, j) => (
                                <div key={j} className="flex items-center gap-2 text-sm">
                                  <CheckCircle2 className="h-4 w-4 text-blue-400" />
                                  <span className="text-zinc-300">{ex.exercise_name}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-zinc-500 italic">Hviledag</p>
                          )}
                        </div>
                      ))}
                    </div>
                    {weeklyWorkoutResult.tips && (
                      <div className="mt-6 p-4 bg-blue-500/5 rounded-xl border border-blue-500/10 flex items-start gap-3">
                        <Zap className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-400">{weeklyWorkoutResult.tips}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Weekly Meal */}
          {activeMode === "weekly-meal" && (
            <div className="space-y-6">
              <Card className="bg-[#18181b] border-white/5 rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-purple-500/10 to-transparent p-6 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <ShoppingCart className="h-5 w-5 text-purple-400" />
                    </div>
                    <div>
                      <h2 style={{ fontFamily: 'Oswald' }} className="font-bold text-xl uppercase text-white">Ugentlig Kostplan</h2>
                      <p className="text-xs text-zinc-500">7 dages madplan med opskrifter og indkøbsliste</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Mål</Label>
                      <Select value={mGoal} onValueChange={setMGoal}>
                        <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="bulking">Bulking (opbygning)</SelectItem>
                          <SelectItem value="cutting">Cutting (vægttab)</SelectItem>
                          <SelectItem value="maintaining">Vedligeholdelse</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Kaloriemål per dag</Label>
                      <Input type="number" value={mCalories} onChange={e => setMCalories(e.target.value)}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-purple-500 rounded-xl h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Kostpræferencer</Label>
                      <Input value={mDiet} onChange={e => setMDiet(e.target.value)} placeholder="f.eks. vegetarisk"
                        className="bg-zinc-900/50 border-zinc-800 focus:border-purple-500 rounded-xl h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400">Måltider per dag</Label>
                      <Select value={mMeals} onValueChange={setMMeals}>
                        <SelectTrigger className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="3">3 måltider</SelectItem>
                          <SelectItem value="4">4 måltider</SelectItem>
                          <SelectItem value="5">5 måltider</SelectItem>
                          <SelectItem value="6">6 måltider</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={generateWeeklyMealplan} disabled={weeklyMealLoading} data-testid="generate-weekly-meal-btn"
                    className="w-full mt-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:opacity-90 rounded-xl font-bold uppercase tracking-wider h-12 text-sm">
                    {weeklyMealLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Genererer (~1 min)...</> : <><ShoppingCart className="mr-2 h-4 w-4" /> Generer ugeplan</>}
                  </Button>
                </CardContent>
              </Card>

              {weeklyMealLoading && <AILoadingState message="Genererer din ugentlige madplan med opskrifter" estimatedTime={60} />}

              {weeklyMealResult && (
                <Card className="bg-[#18181b] border-purple-500/20 rounded-2xl overflow-hidden" data-testid="weekly-meal-result">
                  <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-purple-500/10 to-transparent border-b border-white/5">
                    <CardTitle style={{ fontFamily: 'Oswald' }} className="uppercase text-xl text-white">{weeklyMealResult.name}</CardTitle>
                    <Button onClick={saveWeeklyMealplan} disabled={savingWeeklyMeal} data-testid="save-weekly-meal-btn"
                      className="bg-purple-500 text-white hover:bg-purple-600 rounded-lg">
                      {savingWeeklyMeal ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Gem</>}
                    </Button>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Weekly totals */}
                    {weeklyMealResult.weekly_totals && (
                      <div className="grid grid-cols-4 gap-3 mb-6">
                        {[
                          { label: "Gns. Kcal", value: weeklyMealResult.weekly_totals.avg_calories, color: "from-[#DFFF00]/20", text: "text-[#DFFF00]" },
                          { label: "Protein", value: `${weeklyMealResult.weekly_totals.avg_protein}g`, color: "from-blue-500/20", text: "text-blue-400" },
                          { label: "Kulhydrat", value: `${weeklyMealResult.weekly_totals.avg_carbs}g`, color: "from-orange-500/20", text: "text-orange-400" },
                          { label: "Fedt", value: `${weeklyMealResult.weekly_totals.avg_fat}g`, color: "from-pink-500/20", text: "text-pink-400" },
                        ].map(({ label, value, color, text }) => (
                          <div key={label} className={`text-center p-4 bg-gradient-to-b ${color} to-transparent rounded-xl`}>
                            <div style={{ fontFamily: 'JetBrains Mono' }} className={`font-bold text-xl ${text}`}>{value}</div>
                            <div className="text-[10px] uppercase text-zinc-500 mt-1">{label}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Days */}
                    <div className="space-y-3">
                      {weeklyMealResult.days?.map((day, i) => (
                        <details key={i} className="bg-zinc-900/50 rounded-xl border border-white/5 group">
                          <summary className="p-4 cursor-pointer flex items-center justify-between">
                            <span style={{ fontFamily: 'Oswald' }} className="font-bold text-white uppercase">{day.day}</span>
                            <span style={{ fontFamily: 'JetBrains Mono' }} className="text-sm text-purple-400">{day.total_calories} kcal</span>
                          </summary>
                          <div className="px-4 pb-4 space-y-3">
                            {day.meals?.map((meal, j) => (
                              <div key={j} className="bg-black/30 rounded-xl p-4 border border-white/5">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="font-bold text-white">{meal.name}</span>
                                  <span style={{ fontFamily: 'JetBrains Mono' }} className="text-xs text-purple-400 bg-purple-500/10 px-2 py-1 rounded">
                                    {meal.calories} kcal
                                  </span>
                                </div>
                                {meal.ingredients?.length > 0 && (
                                  <div className="grid grid-cols-2 gap-2 mb-3">
                                    {meal.ingredients.map((ing, k) => (
                                      <div key={k} className="flex justify-between text-xs bg-zinc-900/50 rounded px-2 py-1.5">
                                        <span className="text-zinc-300">{ing.name}</span>
                                        <span className="text-purple-400">{ing.amount}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {meal.recipe && (
                                  <p className="text-xs text-zinc-400 bg-purple-500/5 rounded-lg p-3">{meal.recipe}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      ))}
                    </div>

                    {/* Shopping list */}
                    {weeklyMealResult.shopping_list?.length > 0 && (
                      <ShoppingListCard items={weeklyMealResult.shopping_list} />
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

        </div>
      </div>
    </Paywall>
  );
}
